import { useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, RefreshCw } from "lucide-react";

import { GlobalAppMenu } from "@/components/nav/GlobalAppMenu";
import { useOpsDashboardQuery } from "@/features/workbench/queries";
import { apiClient } from "@/services/api/client";

function text(value: unknown) {
  const raw = String(value ?? "").trim();
  return raw ? raw : "--";
}

function numberText(value: unknown) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    return "--";
  }
  return parsed.toLocaleString("zh-CN");
}

function pctText(value: unknown) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    return "--";
  }
  return `${parsed.toFixed(2)}%`;
}

function tradeDateText(value: unknown) {
  const raw = String(value ?? "").trim();
  if (/^\d{8}$/.test(raw)) {
    return `${raw.slice(0, 4)}-${raw.slice(4, 6)}-${raw.slice(6, 8)}`;
  }
  return raw || "--";
}

function statusToneClass(value: unknown) {
  const status = String(value ?? "").toUpperCase();
  if (["OK", "COMPLETED", "ACTIVE", "SUCCESS"].includes(status)) {
    return "text-emerald-300";
  }
  if (["WARN", "PARTIAL", "RUNNING", "QUEUED", "ACK", "NEW"].includes(status)) {
    return "text-amber-300";
  }
  if (["ERROR", "FAILED", "STALE", "MISSING", "EMPTY", "RESOLVED"].includes(status)) {
    return "text-rose-300";
  }
  return "text-slate-300";
}

export function OpsDashboardPage() {
  const queryClient = useQueryClient();
  const dashboardQuery = useOpsDashboardQuery(false);
  const [timelineKeyword, setTimelineKeyword] = useState("");
  const [timelineLimit, setTimelineLimit] = useState(120);
  const [logView, setLogView] = useState<"quality" | "update">("quality");

  const forceRefreshMutation = useMutation({
    mutationFn: () => apiClient.getOpsDashboard({ refresh: true }),
    onSuccess: (payload) => {
      queryClient.setQueryData(["ops", "dashboard", "cache"], payload);
    },
  });

  const data = dashboardQuery.data;
  const statusData = data?.status_data || { rows: [], overview: {} };
  const logsData = data?.logs_data || { update_logs: [], quality_runs: [] };
  const summary = data?.summary_data || {};
  const overview = (statusData.overview || {}) as Record<string, unknown>;
  const timeline = data?.timeline_data || { rows: [] };
  const freshness = data?.freshness_data || { rows: [] };
  const sourceHealth = data?.source_health_data || { rows: [] };
  const alerts = data?.alerts_data || { rows: [] };

  const timelineRows = useMemo(() => {
    const keyword = timelineKeyword.trim().toUpperCase();
    const rows = (timeline.rows || []).filter((item) => {
      if (!keyword) {
        return true;
      }
      const haystack = [
        item.event_time,
        item.event_type,
        item.source,
        item.task_name,
        item.table_name,
        item.status,
        item.severity,
        item.error_message,
      ]
        .map((part) => String(part ?? "").toUpperCase())
        .join("\n");
      return haystack.includes(keyword);
    });
    return rows.slice(0, Math.max(20, Math.min(300, timelineLimit)));
  }, [timeline.rows, timelineKeyword, timelineLimit]);

  const pageError =
    (dashboardQuery.error as Error | null)?.message ||
    (forceRefreshMutation.error as Error | null)?.message ||
    "";

  return (
    <main className="flex h-dvh min-h-0 flex-col overflow-hidden bg-[var(--bg)] text-[var(--text)]">
      <header className="border-b border-[var(--border)] bg-[#101318]">
        <div className="flex h-12 items-center gap-3 px-3">
          <GlobalAppMenu className="shrink-0" />
          <div className="min-w-0">
            <h1 className="text-sm font-semibold text-slate-100">运维看板</h1>
            <p className="text-[11px] text-[var(--muted)]">系统健康 / 覆盖状态 / 告警与事件时间线</p>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <button
              className="flex h-8 items-center gap-1 rounded-md border border-[var(--border)] bg-[#0c0f14] px-2 text-xs text-[var(--muted)] hover:text-slate-100 disabled:opacity-45"
              disabled={dashboardQuery.isFetching || forceRefreshMutation.isPending}
              onClick={() => {
                if (forceRefreshMutation.isPending) {
                  return;
                }
                forceRefreshMutation.mutate();
              }}
              type="button"
            >
              <RefreshCw className="size-3.5" />
              强制刷新
            </button>
          </div>
        </div>
      </header>

      {pageError ? (
        <div className="border-b border-rose-500/30 bg-rose-500/10 px-3 py-2 text-xs text-rose-200">{pageError}</div>
      ) : null}

      <div className="terminal-scrollbar min-h-0 flex-1 overflow-auto p-3">
        <section className="mb-3 grid grid-cols-5 gap-2 text-xs">
          <div className="rounded-md border border-[var(--border)] bg-[#10151d] px-2 py-1.5">
            <div className="text-[10px] text-[var(--muted)]">最新事件</div>
            <div className="font-medium text-slate-100">{text(summary.latest_event_time)}</div>
          </div>
          <div className="rounded-md border border-[var(--border)] bg-[#10151d] px-2 py-1.5">
            <div className="text-[10px] text-[var(--muted)]">成功率</div>
            <div className="font-medium text-slate-100">{pctText(summary.success_rate)}</div>
          </div>
          <div className="rounded-md border border-[var(--border)] bg-[#10151d] px-2 py-1.5">
            <div className="text-[10px] text-[var(--muted)]">失败事件</div>
            <div className="font-medium text-slate-100">{numberText(summary.failed_events)}</div>
          </div>
          <div className="rounded-md border border-[var(--border)] bg-[#10151d] px-2 py-1.5">
            <div className="text-[10px] text-[var(--muted)]">核心滞后(天)</div>
            <div className="font-medium text-slate-100">{numberText(summary.core_lag_days)}</div>
          </div>
          <div className="rounded-md border border-[var(--border)] bg-[#10151d] px-2 py-1.5">
            <div className="text-[10px] text-[var(--muted)]">生成时间</div>
            <div className="font-medium text-slate-100">{text(data?.generated_at || summary.generated_at)}</div>
          </div>
        </section>

        <section className="mb-3 grid grid-cols-3 gap-2">
          <div className="rounded-md border border-[var(--border)] bg-[#0f1115] p-2">
            <div className="text-[11px] text-[var(--muted)]">股票总数 / 上市中</div>
            <div className="mt-1 text-lg font-semibold text-slate-100">
              {numberText(overview.stock_total_count)} / {numberText(overview.listed_stock_count)}
            </div>
          </div>
          <div className="rounded-md border border-[var(--border)] bg-[#0f1115] p-2">
            <div className="text-[11px] text-[var(--muted)]">最新交易日与覆盖</div>
            <div className="mt-1 text-lg font-semibold text-slate-100">
              {tradeDateText(overview.latest_trade_date)}
            </div>
            <div className="mt-1 text-[11px] text-[var(--muted)]">
              price {numberText(overview.latest_price_count)} · indicator {numberText(overview.latest_indicator_count)}
            </div>
          </div>
          <div className="rounded-md border border-[var(--border)] bg-[#0f1115] p-2">
            <div className="text-[11px] text-[var(--muted)]">累计行数</div>
            <div className="mt-1 text-lg font-semibold text-slate-100">
              {numberText(overview.daily_price_total_rows)} / {numberText(overview.daily_indicator_total_rows)}
            </div>
            <div className="mt-1 text-[11px] text-[var(--muted)]">{text(overview.daily_price_date_range)}</div>
          </div>
        </section>

        <section className="mb-3 grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-3">
          <div className="min-h-0 rounded-md border border-[var(--border)] bg-[#0f1115]">
            <div className="flex items-center justify-between border-b border-[var(--border)] px-2 py-1.5">
              <h2 className="text-xs font-semibold text-slate-200">当前告警</h2>
              <span className="text-[11px] text-[var(--muted)]">{(alerts.rows || []).length} 条</span>
            </div>
            <div className="terminal-scrollbar max-h-[260px] overflow-auto">
              <table className="w-full border-collapse text-[11px]">
                <thead className="sticky top-0 z-10 bg-[#141920] text-slate-300">
                  <tr>
                    <th className="border-b border-[var(--border)] px-2 py-1 text-left font-medium">等级</th>
                    <th className="border-b border-[var(--border)] px-2 py-1 text-left font-medium">规则</th>
                    <th className="border-b border-[var(--border)] px-2 py-1 text-left font-medium">标题</th>
                    <th className="border-b border-[var(--border)] px-2 py-1 text-left font-medium">最近出现</th>
                  </tr>
                </thead>
                <tbody>
                  {(alerts.rows || []).map((row) => (
                    <tr key={String(row.id)} className="border-b border-[var(--border)]/60">
                      <td className={`px-2 py-1 ${statusToneClass(row.severity)}`}>{text(row.severity)}</td>
                      <td className="px-2 py-1 text-slate-300">{text(row.rule_key)}</td>
                      <td className="px-2 py-1 text-slate-200">{text(row.title)}</td>
                      <td className="px-2 py-1 text-slate-400">{text(row.last_seen)}</td>
                    </tr>
                  ))}
                  {!(alerts.rows || []).length ? (
                    <tr>
                      <td className="px-2 py-6 text-center text-[11px] text-[var(--muted)]" colSpan={4}>
                        当前无未恢复告警
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          </div>

          <div className="min-h-0 rounded-md border border-[var(--border)] bg-[#0f1115]">
            <div className="border-b border-[var(--border)] px-2 py-1.5">
              <h2 className="text-xs font-semibold text-slate-200">数据新鲜度矩阵</h2>
            </div>
            <div className="terminal-scrollbar max-h-[260px] overflow-auto">
              <table className="w-full border-collapse text-[11px]">
                <thead className="sticky top-0 z-10 bg-[#141920] text-slate-300">
                  <tr>
                    <th className="border-b border-[var(--border)] px-2 py-1 text-left font-medium">表</th>
                    <th className="border-b border-[var(--border)] px-2 py-1 text-left font-medium">最新交易日</th>
                    <th className="border-b border-[var(--border)] px-2 py-1 text-left font-medium">滞后</th>
                    <th className="border-b border-[var(--border)] px-2 py-1 text-left font-medium">状态</th>
                  </tr>
                </thead>
                <tbody>
                  {(freshness.rows || []).map((row) => (
                    <tr key={String(row.table_name)} className="border-b border-[var(--border)]/60">
                      <td className="px-2 py-1 text-slate-200">{text(row.table_name)}</td>
                      <td className="px-2 py-1 text-slate-300">{tradeDateText(row.latest_trade_date)}</td>
                      <td className="px-2 py-1 text-slate-300">{row.lag_days == null ? "--" : `${numberText(row.lag_days)} 天`}</td>
                      <td className={`px-2 py-1 font-semibold ${statusToneClass(row.status)}`}>{text(row.status)}</td>
                    </tr>
                  ))}
                  {!(freshness.rows || []).length ? (
                    <tr>
                      <td className="px-2 py-6 text-center text-[11px] text-[var(--muted)]" colSpan={4}>
                        暂无新鲜度数据
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        <section className="mb-3 rounded-md border border-[var(--border)] bg-[#0f1115]">
          <div className="border-b border-[var(--border)] px-2 py-1.5">
            <h2 className="text-xs font-semibold text-slate-200">数据源健康（24h）</h2>
          </div>
          <div className="terminal-scrollbar max-h-[220px] overflow-auto">
            <table className="w-full border-collapse text-[11px]">
              <thead className="sticky top-0 z-10 bg-[#141920] text-slate-300">
                <tr>
                  <th className="border-b border-[var(--border)] px-2 py-1 text-left font-medium">来源</th>
                  <th className="border-b border-[var(--border)] px-2 py-1 text-left font-medium">事件数</th>
                  <th className="border-b border-[var(--border)] px-2 py-1 text-left font-medium">成功率</th>
                  <th className="border-b border-[var(--border)] px-2 py-1 text-left font-medium">失败</th>
                  <th className="border-b border-[var(--border)] px-2 py-1 text-left font-medium">P95(ms)</th>
                  <th className="border-b border-[var(--border)] px-2 py-1 text-left font-medium">主要错误</th>
                </tr>
              </thead>
              <tbody>
                {(sourceHealth.rows || []).map((row) => (
                  <tr key={String(row.source)} className="border-b border-[var(--border)]/60">
                    <td className="px-2 py-1 text-slate-200">{text(row.source)}</td>
                    <td className="px-2 py-1 text-slate-300">{numberText(row.event_count)}</td>
                    <td className={`px-2 py-1 ${Number(row.success_rate || 0) >= 90 ? "text-emerald-300" : "text-rose-300"}`}>
                      {pctText(row.success_rate)}
                    </td>
                    <td className="px-2 py-1 text-slate-300">{numberText(row.failed_count)}</td>
                    <td className="px-2 py-1 text-slate-300">{numberText(row.p95_duration_ms)}</td>
                    <td className="px-2 py-1 text-slate-400">
                      {Array.isArray(row.top_errors) && row.top_errors.length
                        ? row.top_errors.map((item: any) => `${item.message}(${item.count})`).join(" ; ")
                        : "--"}
                    </td>
                  </tr>
                ))}
                {!(sourceHealth.rows || []).length ? (
                  <tr>
                    <td className="px-2 py-6 text-center text-[11px] text-[var(--muted)]" colSpan={6}>
                      暂无数据源健康数据
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </section>

        <section className="mb-3 rounded-md border border-[var(--border)] bg-[#0f1115]">
          <div className="flex items-center gap-2 border-b border-[var(--border)] px-2 py-1.5">
            <h2 className="text-xs font-semibold text-slate-200">统一事件时间线</h2>
            <input
              className="ml-2 h-7 w-52 rounded border border-[var(--border)] bg-[#0c0f14] px-2 text-[11px] text-slate-100"
              value={timelineKeyword}
              onChange={(event) => setTimelineKeyword(event.target.value)}
              placeholder="搜索事件/来源/表名"
            />
            <select
              className="h-7 rounded border border-[var(--border)] bg-[#0c0f14] px-2 text-[11px] text-slate-100"
              value={timelineLimit}
              onChange={(event) => setTimelineLimit(Number(event.target.value) || 120)}
            >
              <option value={60}>60</option>
              <option value={120}>120</option>
              <option value={200}>200</option>
            </select>
          </div>
          <div className="terminal-scrollbar max-h-[300px] overflow-auto">
            <table className="w-full border-collapse text-[11px]">
              <thead className="sticky top-0 z-10 bg-[#141920] text-slate-300">
                <tr>
                  <th className="border-b border-[var(--border)] px-2 py-1 text-left font-medium">时间</th>
                  <th className="border-b border-[var(--border)] px-2 py-1 text-left font-medium">类型</th>
                  <th className="border-b border-[var(--border)] px-2 py-1 text-left font-medium">来源</th>
                  <th className="border-b border-[var(--border)] px-2 py-1 text-left font-medium">任务</th>
                  <th className="border-b border-[var(--border)] px-2 py-1 text-left font-medium">状态</th>
                  <th className="border-b border-[var(--border)] px-2 py-1 text-left font-medium">耗时(ms)</th>
                  <th className="border-b border-[var(--border)] px-2 py-1 text-left font-medium">错误</th>
                </tr>
              </thead>
              <tbody>
                {timelineRows.map((row, index) => (
                  <tr key={`${String(row.id || index)}:${String(row.event_time || "")}`} className="border-b border-[var(--border)]/60">
                    <td className="px-2 py-1 text-slate-300">{text(row.event_time)}</td>
                    <td className="px-2 py-1 text-slate-300">{text(row.event_type)}</td>
                    <td className="px-2 py-1 text-slate-300">{text(row.source)}</td>
                    <td className="px-2 py-1 text-slate-200">{text(row.task_name)}</td>
                    <td className={`px-2 py-1 font-semibold ${statusToneClass(row.status)}`}>{text(row.status)}</td>
                    <td className="px-2 py-1 text-slate-300">{numberText(row.duration_ms)}</td>
                    <td className="px-2 py-1 text-slate-400">{text(row.error_message)}</td>
                  </tr>
                ))}
                {!timelineRows.length ? (
                  <tr>
                    <td className="px-2 py-6 text-center text-[11px] text-[var(--muted)]" colSpan={7}>
                      暂无事件
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </section>

        <section className="mb-3 rounded-md border border-[var(--border)] bg-[#0f1115]">
          <div className="border-b border-[var(--border)] px-2 py-1.5">
            <h2 className="text-xs font-semibold text-slate-200">最近交易日数据状态</h2>
          </div>
          <div className="terminal-scrollbar max-h-[240px] overflow-auto">
            <table className="w-full border-collapse text-[11px]">
              <thead className="sticky top-0 z-10 bg-[#141920] text-slate-300">
                <tr>
                  <th className="border-b border-[var(--border)] px-2 py-1 text-left font-medium">交易日</th>
                  <th className="border-b border-[var(--border)] px-2 py-1 text-left font-medium">应覆盖数</th>
                  <th className="border-b border-[var(--border)] px-2 py-1 text-left font-medium">daily_price</th>
                  <th className="border-b border-[var(--border)] px-2 py-1 text-left font-medium">daily_indicator</th>
                  <th className="border-b border-[var(--border)] px-2 py-1 text-left font-medium">price缺失</th>
                  <th className="border-b border-[var(--border)] px-2 py-1 text-left font-medium">indicator缺失</th>
                </tr>
              </thead>
              <tbody>
                {(statusData.rows || []).map((row) => (
                  <tr key={String(row.trade_date)} className="border-b border-[var(--border)]/60">
                    <td className="px-2 py-1 text-slate-200">{tradeDateText(row.trade_date)}</td>
                    <td className="px-2 py-1 text-slate-300">{numberText(row.expected_count)}</td>
                    <td className="px-2 py-1 text-slate-300">{numberText(row.price_count)}</td>
                    <td className="px-2 py-1 text-slate-300">{numberText(row.indicator_count)}</td>
                    <td className={`px-2 py-1 ${Number(row.missing_price_count || 0) > 10 ? "text-rose-300" : "text-emerald-300"}`}>
                      {numberText(row.missing_price_count)}
                    </td>
                    <td className={`px-2 py-1 ${Number(row.missing_indicator_count || 0) > 10 ? "text-rose-300" : "text-emerald-300"}`}>
                      {numberText(row.missing_indicator_count)}
                    </td>
                  </tr>
                ))}
                {!(statusData.rows || []).length ? (
                  <tr>
                    <td className="px-2 py-6 text-center text-[11px] text-[var(--muted)]" colSpan={6}>
                      暂无数据
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </section>

        <section className="rounded-md border border-[var(--border)] bg-[#0f1115]">
          <div className="flex items-center gap-2 border-b border-[var(--border)] px-2 py-1.5">
            <h2 className="text-xs font-semibold text-slate-200">补数与更新日志</h2>
            <button
              className={`rounded px-2 py-0.5 text-[11px] ${logView === "quality" ? "bg-sky-500/20 text-sky-200" : "text-[var(--muted)]"}`}
              onClick={() => setLogView("quality")}
              type="button"
            >
              校验记录
            </button>
            <button
              className={`rounded px-2 py-0.5 text-[11px] ${logView === "update" ? "bg-sky-500/20 text-sky-200" : "text-[var(--muted)]"}`}
              onClick={() => setLogView("update")}
              type="button"
            >
              更新日志
            </button>
          </div>
          <div className="terminal-scrollbar max-h-[280px] overflow-auto">
            {logView === "quality" ? (
              <table className="w-full border-collapse text-[11px]">
                <thead className="sticky top-0 z-10 bg-[#141920] text-slate-300">
                  <tr>
                    <th className="border-b border-[var(--border)] px-2 py-1 text-left font-medium">ID</th>
                    <th className="border-b border-[var(--border)] px-2 py-1 text-left font-medium">日期</th>
                    <th className="border-b border-[var(--border)] px-2 py-1 text-left font-medium">批次</th>
                    <th className="border-b border-[var(--border)] px-2 py-1 text-left font-medium">状态</th>
                    <th className="border-b border-[var(--border)] px-2 py-1 text-left font-medium">补齐</th>
                    <th className="border-b border-[var(--border)] px-2 py-1 text-left font-medium">剩余缺失</th>
                  </tr>
                </thead>
                <tbody>
                  {(logsData.quality_runs || []).map((row) => (
                    <tr key={String(row.id)} className="border-b border-[var(--border)]/60">
                      <td className="px-2 py-1 text-slate-300">{text(row.id)}</td>
                      <td className="px-2 py-1 text-slate-200">{tradeDateText(row.target_date)}</td>
                      <td className="px-2 py-1 text-slate-300">{text(row.run_label)}</td>
                      <td className={`px-2 py-1 ${statusToneClass(row.status)}`}>{text(row.status)}</td>
                      <td className="px-2 py-1 text-slate-300">{numberText(row.filled_count)}</td>
                      <td className="px-2 py-1 text-slate-300">{numberText(row.after_missing_count)}</td>
                    </tr>
                  ))}
                  {!(logsData.quality_runs || []).length ? (
                    <tr>
                      <td className="px-2 py-6 text-center text-[11px] text-[var(--muted)]" colSpan={6}>
                        暂无校验记录
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            ) : (
              <table className="w-full border-collapse text-[11px]">
                <thead className="sticky top-0 z-10 bg-[#141920] text-slate-300">
                  <tr>
                    <th className="border-b border-[var(--border)] px-2 py-1 text-left font-medium">ID</th>
                    <th className="border-b border-[var(--border)] px-2 py-1 text-left font-medium">表</th>
                    <th className="border-b border-[var(--border)] px-2 py-1 text-left font-medium">动作</th>
                    <th className="border-b border-[var(--border)] px-2 py-1 text-left font-medium">状态</th>
                    <th className="border-b border-[var(--border)] px-2 py-1 text-left font-medium">记录数</th>
                    <th className="border-b border-[var(--border)] px-2 py-1 text-left font-medium">错误</th>
                  </tr>
                </thead>
                <tbody>
                  {(logsData.update_logs || []).map((row) => (
                    <tr key={String(row.id)} className="border-b border-[var(--border)]/60">
                      <td className="px-2 py-1 text-slate-300">{text(row.id)}</td>
                      <td className="px-2 py-1 text-slate-200">{text(row.table_name)}</td>
                      <td className="px-2 py-1 text-slate-300">{text(row.action)}</td>
                      <td className={`px-2 py-1 ${statusToneClass(row.status)}`}>{text(row.status)}</td>
                      <td className="px-2 py-1 text-slate-300">{numberText(row.record_count)}</td>
                      <td className="px-2 py-1 text-slate-400">{text(row.error_message)}</td>
                    </tr>
                  ))}
                  {!(logsData.update_logs || []).length ? (
                    <tr>
                      <td className="px-2 py-6 text-center text-[11px] text-[var(--muted)]" colSpan={6}>
                        暂无更新日志
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            )}
          </div>
        </section>

        {dashboardQuery.isFetching || forceRefreshMutation.isPending ? (
          <div className="mt-2 flex items-center gap-1 text-[11px] text-[var(--muted)]">
            <AlertTriangle className="size-3.5" />
            数据刷新中...
          </div>
        ) : null}
      </div>
    </main>
  );
}
